// useProductAutocomplete.js
import { useState, useCallback, useRef } from 'react';
import apiClient from '../../services/apiClient';

const PAGE_SIZE = 20;

const withQuery = (base, params = {}) => {
  const query = Object.entries(params)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join('&');
  return query ? `${base}?${query}` : base;
};

// Backs an AutocompleteSelect against /v1/references/:referenceType with server-side search and
// pagination (see reference.search-pagination.patch.md) — scales to hundreds/thousands of
// entries instead of loading the whole dictionary into a <select>.
//
// referenceType: 'products' for the base-products dictionary, 'product-items' for trade
// names/SKUs (see reference.split-products.patch.md) — the two are now fully separate
// dictionaries, so which one to search is just a parameter, not a filter.
export const useProductAutocomplete = ({ referenceType = 'products' } = {}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const searchRef = useRef('');
  const skipRef = useRef(0);

  const fetchPage = useCallback(async (search, skip, append) => {
    const url = withQuery(`references/${referenceType}`, {
      status: 'active',
      search,
      limit: PAGE_SIZE,
      skip
    });
    const result = await apiClient.get(url);
    const page = (result.data && result.data.data) || [];
    const more = !!(result.data && result.data.hasMore);

    setOptions(prev => (append ? [...prev, ...page] : page));
    setHasMore(more);
    skipRef.current = skip + page.length;
  }, [referenceType]);

  const loadInitial = useCallback(async () => {
    searchRef.current = '';
    setLoading(true);
    try {
      await fetchPage('', 0, false);
    } finally {
      setLoading(false);
    }
  }, [fetchPage]);

  const search = useCallback(async (value) => {
    searchRef.current = value;
    setLoading(true);
    try {
      await fetchPage(value, 0, false);
    } finally {
      setLoading(false);
    }
  }, [fetchPage]);

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    try {
      await fetchPage(searchRef.current, skipRef.current, true);
    } finally {
      setLoadingMore(false);
    }
  }, [fetchPage, hasMore, loadingMore]);

  return { options, loading, loadingMore, hasMore, loadInitial, search, loadMore };
};

export default useProductAutocomplete;